import { useMemo } from 'react';

import styled from '@emotion/styled';
import { useScrollObserverRootHeight } from '@kc-console/context';

import { DETAIL_VIEW_ROOT_HEIGHT_OFFSET } from './constants';
import DetailViewSection from './DetailViewSection';

import type { DetailViewSectionContainerProps } from './types';

/**
 * [ DetailView의 props.sections을 랜더링하는 컴포넌트 ]
 *
 * - DetailView에서 주어진 sections 배열을 래핑하는 컴포넌트입니다.
 * - 서비스 개발 시 직접 사용되지 않으며 DetailView 내부에서 사용됩니다.
 * - 각 섹션 간 gap및 각종 종합 스타일을 관리합니다.
 * - 마지막 섹션의 경우 ScrollObserver entry 선택 여부를 지원하기 위해 min-height값을 설정해줍니다.
 */
const DetailViewSectionContainer = ({ sections }: DetailViewSectionContainerProps) => {
  const rootHeight = useScrollObserverRootHeight();
  const rootHeightWithOffset = useMemo(() => {
    return rootHeight ? rootHeight - DETAIL_VIEW_ROOT_HEIGHT_OFFSET : 0;
  }, [rootHeight]);

  return (
    <StyledWrapper lastItemMinHeight={rootHeightWithOffset}>
      {sections.map(section => {
        return (
          <DetailViewSection
            key={section.entryId}
            content={section.content}
            entryId={section.entryId}
            header={section.header}
            headerEndAdornment={section.headerEndAdornment}
            description={section.description}
            headerAction={section.headerAction}
          />
        );
      })}
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')<{ lastItemMinHeight?: number }>`
  display: flex;
  flex-direction: column;
  margin-top: 48px;
  max-width: 100%;
  gap: 48px;
  & > *:last-child {
    min-height: ${({ lastItemMinHeight = 0 }) => `${lastItemMinHeight}px`};
  }
`;

export default DetailViewSectionContainer;
